import { useEffect, useState } from 'react';
import axios from 'axios';
import { Info } from 'lucide-react';
import { HoverTip, TIP_BADGE } from './HoverTip';
import { useLanguage } from '../../contexts/LanguageContext';

// The exact instruction the image generator is given, fetched from the server so it never
// drifts from the real prompt. The base head is always sent; one of the material rules is
// chosen per part (finished-product photo / plain material / hardware); and the per-part
// context line is appended.
//
// It sits beside the Regenerate button now — one per part column — which is why the fetch
// is shared and why the panel is portalled. Twelve columns are twelve of these; they asked
// the server twelve times for one unchanging answer, and each drew a 46rem panel inside a
// table cell that scrolls horizontally, where it was clipped at the cell's edge.
type Prompt = {
  head: string;
  withProduct: string;
  withoutProduct: string;
  hardware: string;
  // The name always goes; each `optional` sentence only when the part has that detail.
  context: { base: string; optional: string[] };
};

// Asked for once per page load, however many of these are on it. The prompt is a property
// of the app, not of a part: the answer is the same for every column and does not change
// while you look at it.
let cached: Promise<Prompt> | null = null;
const loadPrompt = () => {
  cached ??= axios.get<Prompt>('/api/notice/image-prompt').then((r) => r.data);
  return cached;
};

export function PromptTooltip() {
  const { t } = useLanguage();
  const [p, setP] = useState<Prompt | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadPrompt()
      .then((data) => {
        if (!cancelled) setP(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // The prompt as one block of text. Assembled from the same fields the panel draws, in the
  // order it draws them, so the paste reads like what was on screen — the alternative is the
  // panel's own innerText, which carries the captions and the "only when the part has this
  // detail" aside as though they were part of the instruction.
  const asText = p && [
    p.head,
    `${t('notice.step3.promptWithProduct')}: ${p.withProduct}`,
    `${t('notice.step3.promptNoProduct')}: ${p.withoutProduct}`,
    `${t('notice.step3.promptHardware')}: ${p.hardware}`,
    `${t('notice.step3.promptContext')}: ${p.context.base}`,
    ...p.context.optional.map((s) => `  - ${s}`),
  ].join('\n\n');

  return (
    <HoverTip
      copy={asText ?? ''}
      trigger={
        <span className={TIP_BADGE} title={t('notice.step3.promptTitle')}>
          <Info className="h-3.5 w-3.5" />
        </span>
      }
      content={
        <div className="w-[42rem] max-w-[86vw] text-left text-xs leading-relaxed">
          <div className="mb-2 text-center text-sm font-semibold text-slate-900">
            {t('notice.step3.promptTitle')}
          </div>
          {p ? (
          <div className="flex flex-col gap-3">
            <p className="whitespace-pre-line">{p.head}</p>
            <PromptBlock label={t('notice.step3.promptWithProduct')} text={p.withProduct} />
            <PromptBlock label={t('notice.step3.promptNoProduct')} text={p.withoutProduct} />
            <PromptBlock label={t('notice.step3.promptHardware')} text={p.hardware} />
            <div>
              <div className="mb-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-blue-600">
                {t('notice.step3.promptContext')}
              </div>
              <p className="whitespace-pre-line">{p.context.base}</p>
              <p className="mt-1 italic text-slate-400">
                {t('notice.step3.promptContextNote')}
              </p>
              <ul className="mt-1 list-disc pl-4 text-slate-500">
                {p.context.optional.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>
          </div>
          ) : (
            <p className="text-center text-slate-400">…</p>
          )}
        </div>
      }
    />
  );
}

// One labelled paragraph of the prompt: a small caption over the exact text.
function PromptBlock({ label, text }: { label: string; text: string }) {
  return (
    <div>
      <div className="mb-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-blue-600">
        {label}
      </div>
      <p className="whitespace-pre-line">{text}</p>
    </div>
  );
}
